import React, { useState, useEffect } from 'react';
import { Bell, Check, CheckCheck, X, Calendar, UserPlus, ThumbsUp, ThumbsDown, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import nl from 'date-fns/locale/nl';
import api from '../lib/api';


const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    if (open && user) {
      fetchNotifications();
    }
  }, [open, user]);

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/notifications?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Handle both old array format and new object format
      const data = response.data;
      setNotifications(Array.isArray(data) ? data : data.notifications || []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await api.post(`/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? {...n, read: true} : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await api.post(`/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({...n, read: true})));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    // Use 'link' field if available (e.g., freshness checks, story requests)
    if (notification.link) {
      navigate(notification.link);
      setOpen(false);
    } else if (notification.activity_id) {
      navigate(`/activity/${notification.activity_id}`);
      setOpen(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'activity_approved':
        return <ThumbsUp className="w-4 h-4 text-green-500" />;
      case 'activity_rejected':
        return <ThumbsDown className="w-4 h-4 text-red-500" />;
      case 'invitation':
        return <UserPlus className="w-4 h-4 text-brand" />;
      case 'activity_deleted':
        return <Trash2 className="w-4 h-4 text-red-500" />;
      case 'new_participant':
        return <UserPlus className="w-4 h-4 text-sky-600" />;
      case 'organizer_broadcast':
        return <Bell className="w-4 h-4 text-amber-600" />;
      case 'organizer_broadcast_sent':
        return <Check className="w-4 h-4 text-green-600" />;
      case 'freshness_check':
        return <Bell className="w-4 h-4 text-orange-500" />;
      case 'freshness_response':
      case 'freshness_expired':
      case 'activity_expired':
        return <Bell className="w-4 h-4 text-amber-600" />;
      case 'activity_reminder':
      case 'activity_reminder_today':
        return <Bell className="w-4 h-4 text-hoi-blue" />;
      default:
        return <Bell className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'activity_approved':
        return 'bg-green-50 border-green-200';
      case 'activity_rejected':
      case 'activity_deleted':
        return 'bg-red-50 border-red-200';
      case 'invitation':
        return 'bg-brand/5 border-brand/20';
      case 'organizer_broadcast':
        return 'bg-amber-50 border-amber-200';
      case 'organizer_broadcast_sent':
        return 'bg-green-50 border-green-200';
      case 'freshness_check':
        return 'bg-orange-50 border-orange-200';
      case 'freshness_response':
      case 'freshness_expired':
      case 'activity_expired':
        return 'bg-amber-50 border-amber-200';
      default:
        return 'bg-slate-50 border-slate-200';
    }
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-full">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <Badge className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-4 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold text-primary">Meldingen</h3>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-xs text-brand">
                  <CheckCheck className="w-3 h-3 mr-1" />
                  Alles gelezen
                </Button>
              )}
            </div>
          </div>
        </div>
        
        <ScrollArea className="h-[300px]">
          {loading ? (
            <div className="p-4 text-center text-muted-foreground">
              Laden...
            </div>
          ) : notifications.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                    !notification.read ? 'bg-brand/5' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${getNotificationColor(notification.type)}`}>
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!notification.read ? 'font-semibold text-primary' : 'text-primary'}`}>
                        {notification.title}
                      </p>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale: nl })}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="w-2 h-2 rounded-full bg-brand shrink-0 mt-2" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Bell className="w-12 h-12 mx-auto mb-3 text-muted" />
              <p className="text-muted-foreground text-sm">Geen meldingen</p>
            </div>
          )}
        </ScrollArea>
        
        {/* Link to full notification center */}
        <div className="p-3 border-t border-slate-100">
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full text-brand text-sm font-medium" 
            onClick={() => { setOpen(false); navigate('/meldingen'); }}
            data-testid="view-all-notifications-btn"
          >
            Bekijk alle meldingen
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
